import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import evergreenPages from '../../evergreen-pages.json';
import { defaultFilters } from '../lib/consts';
import { fetchRentals, type Rental } from '../lib/rental';
import { RentalContext } from '../contexts/RentalContext';
import RentalCard from './RentalCard';
import { EvergreenLinks } from './EvergreenLinks';
import { Seo } from './Seo';

type EvergreenPage = {
  slug: string;
  title: string;
  h1: string;
  description: string;
  intro: string;
  searchPath: string;
  match: {
    type: 'location' | 'keyword' | 'all';
    terms: string[];
  };
  highlights: string[];
};

const pages = evergreenPages as EvergreenPage[];
const FOOTER_LOCATION_LINKS = ['ubud', 'canggu', 'uluwatu', 'seminyak'];

function matchesPage(rental: Rental, page: EvergreenPage) {
  if (page.match.type === 'all') {
    return true;
  }

  const terms = page.match.terms.map((term) => term.toLowerCase());
  const haystack = [
    rental.location,
    rental.title,
    rental.property_type,
  ].filter(Boolean).join(' ').toLowerCase();

  return terms.some((term) => haystack.includes(term));
}

export default function EvergreenLandingPage() {
  const { slug } = useParams();
  const page = pages.find((candidate) => candidate.slug === slug);
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    if (!page) {
      return;
    }

    setStatus('loading');
    fetchRentals({ ...defaultFilters, dateRange: 'month' })
      .then((results) => {
        setRentals(results.filter((rental) => matchesPage(rental, page)).slice(0, 12));
        setStatus('loaded');
      })
      .catch((error) => {
        console.error('Error loading evergreen rentals:', error);
        setStatus('error');
      });
  }, [page]);

  if (!page) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="text-3xl font-semibold text-gray-900">Rental page not found</h1>
        <Link to="/" className="mt-6 inline-block text-emerald-800 underline">Back to Bali rentals</Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      <Seo title={page.title} description={page.description} canonicalPath={`/rentals/${page.slug}`} />

      <section className="relative overflow-hidden bg-emerald-950 px-6 py-16 text-white">
        <img
          src="/hero-bali.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative mx-auto max-w-5xl">
          <Link to="/" className="text-sm font-medium text-emerald-100 underline underline-offset-4">
            Bali Rental Finder
          </Link>
          <h1 className="mt-6 max-w-3xl font-raleway text-4xl font-extrabold leading-tight md:text-5xl">
            {page.h1}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-emerald-50">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={page.searchPath}
              className="rounded-md bg-orange-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-orange-700"
            >
              Search current listings
            </Link>
            <a
              href="#fresh-listings"
              className="rounded-md border border-white/40 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              See fresh matches
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="grid gap-4 md:grid-cols-3">
          {page.highlights.map((highlight) => (
            <div key={highlight} className="rounded-lg border border-gray-200 bg-white p-4 text-sm leading-6 text-gray-700">
              {highlight}
            </div>
          ))}
        </div>
      </section>

      <section id="fresh-listings" className="mx-auto max-w-6xl px-6 pb-12">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Fresh listings</h2>
            <p className="mt-1 text-sm text-gray-600">Recent matching rentals from the last 30 days.</p>
          </div>
          <Link to={page.searchPath} className="text-sm font-semibold text-emerald-800 underline">
            Open full search
          </Link>
        </div>

        {status === 'loading' && <p className="text-sm text-gray-500">Loading rentals...</p>}
        {status === 'error' && <p className="text-sm text-red-600">Could not load fresh rentals.</p>}
        {status === 'loaded' && rentals.length === 0 && (
          <p className="text-sm text-gray-600">No fresh matches right now. The full search may still have nearby options.</p>
        )}
        {status === 'loaded' && rentals.length > 0 && (
          <RentalContext.Provider value={{ displayCurrency: 'IDR', keywordTerms: page.match.terms }}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rentals.map((rental) => (
                <RentalCard key={rental.id} rental={rental} />
              ))}
            </div>
          </RentalContext.Provider>
        )}
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-14">
        <h2 className="text-xl font-semibold text-gray-900">Browse more Bali rental searches</h2>
        <EvergreenLinks currentSlug={page.slug} slugs={FOOTER_LOCATION_LINKS} />
      </section>
    </main>
  );
}
